import { useTranslation } from 'react-i18next';

import { SearchInput } from '~/components/common/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import type {
  ScheduleHealth,
  ScheduleListFilters,
  ScheduleStatus,
} from '~/features/catalog-operations/types/catalog-ops-schedule.types';

/** Sentinel for "no filter". Radix Select forbids an empty-string item value,
 *  and the service drops the key rather than sending `status=`, which the
 *  server would try to parse as an enum. */
const ANY = 'ANY';

/**
 * Applies one filter change. A cleared filter becomes `undefined`, which the
 * request builder drops rather than sending as an empty enum, and every change
 * resets `cursor` — a new filter means page one.
 */
function withFilter<K extends keyof ScheduleListFilters>(
  filters: ScheduleListFilters,
  key: K,
  value: ScheduleListFilters[K],
): ScheduleListFilters {
  return { ...filters, [key]: value, cursor: undefined };
}

interface Props {
  filters: ScheduleListFilters;
  onChange: (next: ScheduleListFilters) => void;
  /** Row counts for the "showing N of M" hint; omitted while loading. */
  shown?: number | undefined;
  total?: number | undefined;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * Filters for the schedules list. Every control is a fixed set because the API
 * parses `status`, `health` and `connector` as enums — an unrecognized value is
 * a 400, not an ignored filter. The same values are sent to the list and the
 * ribbon so the metrics always describe the rows on screen.
 */
export function SchedulesFilterBar({
  filters,
  onChange,
  shown,
  total,
  testId = 'schedules-filter',
}: Props) {
  const { t } = useTranslation('catalog-operations');

  return (
    <div data-testid={`${testId}-bar`} className="flex flex-wrap items-center gap-2">
      <SearchInput
        value={filters.search ?? ''}
        onChange={(value) => {
          onChange(withFilter(filters, 'search', value || undefined));
        }}
        placeholder={t('schedules.filters.searchPlaceholder')}
        className="w-full sm:w-64"
        testId={`${testId}-search`}
      />

      <Select
        value={filters.status ?? ANY}
        onValueChange={(value) => {
          onChange(
            withFilter(filters, 'status', value === ANY ? undefined : (value as ScheduleStatus)),
          );
        }}
      >
        <SelectTrigger
          data-testid={`${testId}-status-select`}
          className="h-9 w-full cursor-pointer sm:w-36"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent data-testid={`${testId}-status-select-content`}>
          <SelectItem
            value={ANY}
            data-testid={`${testId}-status-option-any`}
            className="cursor-pointer"
          >
            {t('schedules.filters.anyStatus')}
          </SelectItem>
          <SelectItem
            value="ACTIVE"
            data-testid={`${testId}-status-option-active`}
            className="cursor-pointer"
          >
            {t('schedules.status.active')}
          </SelectItem>
          <SelectItem
            value="PAUSED"
            data-testid={`${testId}-status-option-paused`}
            className="cursor-pointer"
          >
            {t('schedules.status.paused')}
          </SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.connector ?? ANY}
        onValueChange={(value) => {
          onChange(withFilter(filters, 'connector', value === ANY ? undefined : value));
        }}
      >
        <SelectTrigger
          data-testid={`${testId}-channel-select`}
          className="h-9 w-full cursor-pointer sm:w-44"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent data-testid={`${testId}-channel-select-content`}>
          <SelectItem
            value={ANY}
            data-testid={`${testId}-channel-option-any`}
            className="cursor-pointer"
          >
            {t('schedules.filters.anyChannel')}
          </SelectItem>
          <SelectItem
            value="GMC"
            data-testid={`${testId}-channel-option-gmc`}
            className="cursor-pointer"
          >
            {t('schedules.connector.GMC')}
          </SelectItem>
          <SelectItem
            value="META"
            data-testid={`${testId}-channel-option-meta`}
            className="cursor-pointer"
          >
            {t('schedules.connector.META')}
          </SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.health ?? ANY}
        onValueChange={(value) => {
          onChange(
            withFilter(filters, 'health', value === ANY ? undefined : (value as ScheduleHealth)),
          );
        }}
      >
        <SelectTrigger
          data-testid={`${testId}-health-select`}
          className="h-9 w-full cursor-pointer sm:w-40"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent data-testid={`${testId}-health-select-content`}>
          <SelectItem
            value={ANY}
            data-testid={`${testId}-health-option-any`}
            className="cursor-pointer"
          >
            {t('schedules.filters.anyHealth')}
          </SelectItem>
          <SelectItem
            value="HEALTHY"
            data-testid={`${testId}-health-option-healthy`}
            className="cursor-pointer"
          >
            {t('schedules.filters.healthy')}
          </SelectItem>
          <SelectItem
            value="FAILING"
            data-testid={`${testId}-health-option-failing`}
            className="cursor-pointer"
          >
            {t('schedules.filters.failing')}
          </SelectItem>
        </SelectContent>
      </Select>

      {shown !== undefined && total !== undefined && shown !== total && (
        <p
          data-testid={`${testId}-showing-count`}
          className="text-muted-foreground ml-auto text-xs"
        >
          {t('schedules.filters.showing', { shown, total })}
        </p>
      )}
    </div>
  );
}
